import {
  MAX_FEEDBACK_CONTEXT_CHARS,
  MAX_FEEDBACK_MESSAGE_CHARS,
  MAX_FEEDBACK_NAME_CHARS,
} from "./limits.js";
import type {
  FeedbackCategory,
  FeedbackContext,
  FeedbackEntry,
  FeedbackExperience,
} from "../types.js";

export type { FeedbackExperience } from "../types.js";

/** The closed set of categories the feedback form and API accept. */
export const FEEDBACK_CATEGORIES = ["bug", "idea", "confusing", "praise"] as const satisfies
  readonly FeedbackCategory[];

/** The closed set of overall-experience answers the feedback form and API accept. */
export const FEEDBACK_EXPERIENCES = ["works_great", "needs_improvement"] as const satisfies
  readonly FeedbackExperience[];

/** Human-readable labels for the overall-experience answers. */
export const FEEDBACK_EXPERIENCE_LABELS: Readonly<Record<FeedbackExperience, string>> = {
  works_great: "Works great",
  needs_improvement: "Needs improvement",
};

const FEEDBACK_CATEGORY_SET: ReadonlySet<string> = new Set(FEEDBACK_CATEGORIES);
const FEEDBACK_EXPERIENCE_SET: ReadonlySet<string> = new Set(FEEDBACK_EXPERIENCES);
const WORKSPACE_ID_PATTERN = /^ws_[A-Za-z0-9_]+$/;

function boundedText(value: unknown, max: number): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : null;
}

function normalizeExperience(value: unknown): FeedbackExperience | null {
  return typeof value === "string" && FEEDBACK_EXPERIENCE_SET.has(value)
    ? (value as FeedbackExperience)
    : null;
}

function normalizeContext(value: unknown): FeedbackContext {
  const source =
    typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return {
    view: boundedText(source.view, MAX_FEEDBACK_CONTEXT_CHARS) ?? "",
    userAgent: boundedText(source.userAgent, MAX_FEEDBACK_CONTEXT_CHARS) ?? "",
    workspaceId:
      typeof source.workspaceId === "string" && WORKSPACE_ID_PATTERN.test(source.workspaceId.trim())
        ? source.workspaceId.trim()
        : null,
  };
}

/**
 * Repairs a persisted feedback entry into the current shape. A missing id,
 * createdAt, message, or an unknown category marks the entry as malformed and
 * it is dropped; everything else is trimmed and clamped, so one bad entry can
 * never quarantine the whole store.
 */
export function normalizeFeedbackEntry(input: unknown): FeedbackEntry | null {
  if (typeof input !== "object" || input === null || Array.isArray(input)) return null;
  const source = input as Record<string, unknown>;

  const id = boundedText(source.id, 200);
  const createdAt = boundedText(source.createdAt, 64);
  const message = boundedText(source.message, MAX_FEEDBACK_MESSAGE_CHARS);
  const category = typeof source.category === "string" ? source.category.trim() : "";

  if (!id || !createdAt || !message || !FEEDBACK_CATEGORY_SET.has(category)) return null;

  return {
    id,
    createdAt,
    message,
    category: category as FeedbackCategory,
    experience: normalizeExperience(source.experience),
    name: boundedText(source.name, MAX_FEEDBACK_NAME_CHARS),
    email: boundedText(source.email, 254),
    context: normalizeContext(source.context),
  };
}
